/**
 * Canonical machine-readable error codes for the LivDot platform.
 * These codes are mapped to string slugs used in ApiError.type URLs.
 */
export const ApiErrorCode = {
  // AUTH errors
  INVALID_CREDENTIALS: 'auth/invalid-credentials',
  TOKEN_EXPIRED: 'auth/token-expired',
  TOKEN_INVALID: 'auth/token-invalid',
  TOKEN_MISSING: 'auth/token-missing',
  UNAUTHORIZED: 'auth/unauthorized',
  FORBIDDEN: 'auth/forbidden',
  EMAIL_NOT_VERIFIED: 'auth/email-not-verified',
  EMAIL_ALREADY_EXISTS: 'auth/email-already-exists',
  COGNITO_ERROR: 'auth/cognito-error',

  // USER errors
  USER_NOT_FOUND: 'user/user-not-found',
  HOST_PROFILE_NOT_FOUND: 'user/host-profile-not-found',
  HOST_PROFILE_ALREADY_EXISTS: 'user/host-profile-already-exists',
  VENDOR_PROFILE_NOT_FOUND: 'user/vendor-profile-not-found',
  VENDOR_PROFILE_ALREADY_EXISTS: 'user/vendor-profile-already-exists',
  VENDOR_NOT_VERIFIED: 'user/vendor-not-verified',
  INVALID_STATE_TRANSITION: 'marketplace/invalid-state-transition',
  REASON_REQUIRED: 'marketplace/reason-required',
  ACCOUNT_SUSPENDED: 'user/account-suspended',
  KYC_REQUIRED: 'user/kyc-required',

  // EVENT errors
  EVENT_NOT_FOUND: 'event/event-not-found',
  EVENT_NOT_PUBLISHED: 'event/event-not-published',
  EVENT_ALREADY_LIVE: 'event/event-already-live',
  EVENT_CANCELLED: 'event/event-cancelled',
  EVENT_ENDED: 'event/event-ended',
  EVENT_STATE_TRANSITION_INVALID: 'event/state-transition-invalid',
  EVENT_PREFLIGHT_FAILED: 'event/preflight-failed',
  EVENT_MAX_TICKETS_REACHED: 'event/max-tickets-reached',
  EVENT_GEO_RESTRICTED: 'event/geo-restricted',
  EVENT_NOT_ACCESSIBLE: 'event/not-accessible',
  EVENT_FEEDBACK_NOT_OPEN: 'event/feedback-not-open',

  // BOOKING errors
  BOOKING_NOT_FOUND: 'booking/booking-not-found',
  BOOKING_STATE_TRANSITION_INVALID: 'booking/state-transition-invalid',
  VENDOR_UNAVAILABLE: 'booking/vendor-unavailable',
  ESCROW_FUNDING_FAILED: 'booking/escrow-funding-failed',
  BOOKING_ALREADY_EXISTS: 'booking/booking-already-exists',
  JOB_LISTING_NOT_FOUND: 'booking/job-listing-not-found',
  JOB_LISTING_CLOSED: 'booking/job-listing-closed',
  INDICATION_OF_INTEREST_DUPLICATE: 'booking/ioi-duplicate',
  INDICATION_OF_INTEREST_NOT_FOUND: 'booking/ioi-not-found',
  HIRING_THREAD_NOT_FOUND: 'booking/hiring-thread-not-found',
  HIRING_THREAD_FORBIDDEN: 'booking/hiring-thread-forbidden',
  REVIEW_ALREADY_EXISTS: 'booking/review-already-exists',
  BOOKING_NOT_REVIEWABLE: 'booking/not-reviewable',
  BOOKING_REVIEW_REQUIRED_BEFORE_RELEASE: 'booking/review-required-before-release',
  CREW_ASSIGNMENT_NOT_FOUND: 'booking/crew-assignment-not-found',
  CREW_ASSIGNMENT_INVALID_STATE: 'booking/crew-assignment-invalid-state',
  CREW_ASSIGNMENT_ALREADY_EXISTS: 'booking/crew-assignment-already-exists',
  CREW_ASSIGNMENT_BOOKING_EXISTS: 'booking/crew-assignment-booking-exists',

  // STREAM errors
  STREAM_NOT_ACTIVE: 'stream/stream-not-active',
  ENTITLEMENT_NOT_FOUND: 'stream/entitlement-not-found',
  ENTITLEMENT_EXPIRED: 'stream/entitlement-expired',
  ENTITLEMENT_REVOKED: 'stream/entitlement-revoked',
  STREAM_CREDENTIALS_UNAVAILABLE: 'stream/credentials-unavailable',
  IVS_ERROR: 'stream/ivs-error',

  // PAYMENT errors
  PAYMENT_NOT_FOUND: 'payment/payment-not-found',
  PAYMENT_ALREADY_PROCESSED: 'payment/already-processed',
  PAYMENT_FAILED: 'payment/payment-failed',
  REFUND_NOT_ELIGIBLE: 'payment/refund-not-eligible',
  PAYOUT_FAILED: 'payment/payout-failed',
  INVALID_AMOUNT: 'payment/invalid-amount',
  PROVIDER_ERROR: 'payment/provider-error',

  // DISPUTE errors
  DISPUTE_NOT_FOUND: 'dispute/dispute-not-found',
  DISPUTE_ALREADY_EXISTS: 'dispute/dispute-already-exists',
  DISPUTE_DEADLINE_EXCEEDED: 'dispute/deadline-exceeded',

  // GENERAL errors
  VALIDATION_ERROR: 'general/validation-error',
  RESOURCE_NOT_FOUND: 'general/resource-not-found',
  CONFLICT: 'general/conflict',
  RATE_LIMITED: 'general/rate-limited',
  INTERNAL_SERVER_ERROR: 'general/internal-server-error',
  SERVICE_UNAVAILABLE: 'general/service-unavailable',
} as const;

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];
